import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  DateInput,
  ErrorText,
  Field,
  formatSum,
  Input,
  Modal,
  Select,
  toSum,
  toTiyin,
} from '@/components/ui';

interface PromoCode {
  id: string;
  code: string;
  discountType: 'PERCENT' | 'FIXED';
  value: number;
  maxDiscount: number | null;
  minOrderAmount: number | null;
  startsAt: string | null;
  endsAt: string | null;
  usageLimit: number | null;
  usageLimitPerUser: number | null;
  usedCount: number;
  isActive: boolean;
  _count: { usages: number };
}

interface Draft {
  id?: string;
  code: string;
  discountType: 'PERCENT' | 'FIXED';
  value: string;
  maxDiscount: string;
  minOrderAmount: string;
  startsAt: string;
  endsAt: string;
  usageLimit: string;
  usageLimitPerUser: string;
}

const EMPTY: Draft = {
  code: '',
  discountType: 'PERCENT',
  value: '',
  maxDiscount: '',
  minOrderAmount: '',
  startsAt: '',
  endsAt: '',
  usageLimit: '',
  usageLimitPerUser: '',
};

export default function PromoPage() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['promo-codes'],
    queryFn: async () => (await api.get<PromoCode[]>('/admin/promo-codes')).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['promo-codes'] });
  const fail = (e: unknown) => setError(errorMessage(e));

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const payload = {
        code: value.code,
        discountType: value.discountType,
        // Процент — числом, фиксированная скидка — в тийинах
        value: value.discountType === 'PERCENT' ? Number(value.value) : toTiyin(value.value),
        maxDiscount: value.maxDiscount ? toTiyin(value.maxDiscount) : null,
        minOrderAmount: value.minOrderAmount ? toTiyin(value.minOrderAmount) : null,
        startsAt: value.startsAt || null,
        endsAt: value.endsAt || null,
        usageLimit: value.usageLimit ? Number(value.usageLimit) : null,
        usageLimitPerUser: value.usageLimitPerUser ? Number(value.usageLimitPerUser) : null,
      };

      return value.id
        ? api.patch(`/admin/promo-codes/${value.id}`, payload)
        : api.post('/admin/promo-codes', payload);
    },
    onSuccess: () => {
      setDraft(null);
      setError(null);
      void invalidate();
    },
    onError: fail,
  });

  const toggle = useMutation({
    mutationFn: (promo: PromoCode) =>
      api.patch(`/admin/promo-codes/${promo.id}`, { isActive: !promo.isActive }),
    onSuccess: invalidate,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/promo-codes/${id}`),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: fail,
  });

  const discountLabel = (promo: PromoCode) =>
    promo.discountType === 'PERCENT' ? `${promo.value}%` : formatSum(promo.value);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Промокоды</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Скидка применяется к аренде и аксессуарам, доставку и залог не трогает
          </p>
        </div>
        <Button onClick={() => setDraft({ ...EMPTY })}>Новый промокод</Button>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <Card>
        {isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-neutral-500">Промокодов пока нет</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500">
                <tr>
                  <th className="pb-2 font-medium">Код</th>
                  <th className="pb-2 font-medium">Скидка</th>
                  <th className="pb-2 font-medium">Потолок</th>
                  <th className="pb-2 font-medium">От суммы</th>
                  <th className="pb-2 font-medium">Срок</th>
                  <th className="pb-2 font-medium">Использован</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((promo) => (
                  <tr
                    key={promo.id}
                    className="border-t border-neutral-100 dark:border-neutral-800"
                  >
                    <td className="py-2 font-mono">
                      {promo.code}{' '}
                      {promo.isActive ? (
                        <Badge tone="green">вкл</Badge>
                      ) : (
                        <Badge tone="red">выкл</Badge>
                      )}
                    </td>
                    <td className="py-2">{discountLabel(promo)}</td>
                    <td className="py-2">{formatSum(promo.maxDiscount)}</td>
                    <td className="py-2">{formatSum(promo.minOrderAmount)}</td>
                    <td className="py-2 text-xs text-neutral-500">
                      {promo.endsAt
                        ? new Date(promo.endsAt).toLocaleDateString('ru-RU')
                        : 'бессрочно'}
                    </td>
                    <td className="py-2">
                      {promo.usedCount}
                      {promo.usageLimit ? ` / ${promo.usageLimit}` : ''}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          setDraft({
                            id: promo.id,
                            code: promo.code,
                            discountType: promo.discountType,
                            value:
                              promo.discountType === 'PERCENT'
                                ? String(promo.value)
                                : toSum(promo.value),
                            maxDiscount: toSum(promo.maxDiscount),
                            minOrderAmount: toSum(promo.minOrderAmount),
                            startsAt: promo.startsAt?.slice(0, 10) ?? '',
                            endsAt: promo.endsAt?.slice(0, 10) ?? '',
                            usageLimit: promo.usageLimit ? String(promo.usageLimit) : '',
                            usageLimitPerUser: promo.usageLimitPerUser
                              ? String(promo.usageLimitPerUser)
                              : '',
                          })
                        }
                      >
                        Изм.
                      </Button>
                      <Button variant="ghost" onClick={() => toggle.mutate(promo)}>
                        {promo.isActive ? 'Выкл' : 'Вкл'}
                      </Button>
                      <Button
                        variant="ghost"
                        className="text-red-600"
                        onClick={() => remove.mutate(promo.id)}
                      >
                        Удал.
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={Boolean(draft)}
        title={draft?.id ? 'Изменить промокод' : 'Новый промокод'}
        onClose={() => setDraft(null)}
      >
        {draft && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Код">
                <Input
                  value={draft.code}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                  placeholder="AUTUMN20"
                />
              </Field>

              <Field label="Тип скидки">
                <Select
                  value={draft.discountType}
                  onChange={(e) =>
                    setDraft({ ...draft, discountType: e.target.value as Draft['discountType'] })
                  }
                >
                  <option value="PERCENT">Процент</option>
                  <option value="FIXED">Фиксированная сумма</option>
                </Select>
              </Field>

              <Field label={draft.discountType === 'PERCENT' ? 'Процент' : 'Сумма, сум'}>
                <Input
                  value={draft.value}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                />
              </Field>

              <Field label="Потолок скидки, сум" hint="Пусто — без ограничения">
                <Input
                  value={draft.maxDiscount}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, maxDiscount: e.target.value })}
                />
              </Field>

              <Field label="Минимальная сумма заказа, сум">
                <Input
                  value={draft.minOrderAmount}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, minOrderAmount: e.target.value })}
                />
              </Field>

              <Field label="Действует с">
                <DateInput
                  value={draft.startsAt}
                  onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
                />
              </Field>

              <Field label="Действует по">
                <DateInput
                  value={draft.endsAt}
                  onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
                />
              </Field>

              <Field label="Всего применений">
                <Input
                  value={draft.usageLimit}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, usageLimit: e.target.value })}
                />
              </Field>

              <Field label="На одного клиента">
                <Input
                  value={draft.usageLimitPerUser}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, usageLimitPerUser: e.target.value })}
                />
              </Field>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDraft(null)}>
                Отмена
              </Button>
              <Button disabled={save.isPending} onClick={() => save.mutate(draft)}>
                {save.isPending ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
